/**
 * bc-doc-zoom — fold product-docs images into Horizon's existing zoom dialog.
 *
 * Horizon's <media-gallery> ships a <zoom-dialog> whose zoomable list is built
 * from <li ref="media[]"> slides plus a parallel thumbnail strip, all rendered
 * server-side from product.media. Clicking a gallery image calls
 * zoomDialog.open(index), which indexes into refs.media[index].
 *
 * The product-docs panel (sections/bc-product-docs.liquid) renders its own
 * images OUTSIDE that gallery — plain <img> in image blocks and description
 * prose — so they are neither clickable nor part of the zoom list.
 *
 * This script appends those doc images into the SAME zoom-dialog at runtime:
 * it CLONES an existing image slide (so the drag-zoom-wrapper / product-media
 * structure and all wiring come along for free), swaps in the doc image's URL,
 * and appends a matching thumbnail. Horizon's Component base class runs a
 * MutationObserver that re-collects ref="media[]" whenever the dialog subtree
 * changes (assets/component.js), so the appended slides join refs.media with no
 * change to any upstream file. Each doc image is then wired (via one delegated
 * listener) to open the dialog at its appended index.
 *
 * Pure addition — touches no Horizon file. Couples only to the stable contract
 * (ref="media[]", ref="thumbnails", zoomDialog.open(index)); the per-slide
 * markup is cloned, not hand-authored, so it tracks upstream automatically.
 *
 * bc- prefix per the repo's customization conventions (see CLAUDE.md).
 */

const DOCS_ROOT_ID = 'product-docs';
// Image-block photos + description/prose images. NOT sewing pattern figures
// (.bc-sewing__figure) and not inline SVG diagrams/charts (those aren't <img>).
const DOC_IMAGE_SELECTOR = '.bc-doc-block--image img, .bc-product-docs__prose img';
const MIN_ZOOMABLE_WIDTH = 80; // px — skip tiny inline icons/badges
const HI_RES_WIDTH = 2048; // zoom slide source width (Shopify caps at original)
const THUMB_WIDTH = 256; // thumbnail strip source width
const INDEX_ATTR = 'data-bc-doc-zoom-index';
const SLIDE_MARK = 'data-bc-doc-zoom-slide';
const THUMB_MARK = 'data-bc-doc-zoom-thumb';
// Records how many zoom slides are real gallery media, so the desync guard can
// tell our appended doc slides (index >= count) from gallery ones.
const GALLERY_COUNT_ATTR = 'data-bc-gallery-media-count';

/**
 * Find the MAIN product gallery's zoom dialog, once it's upgraded and openable.
 * A zoom-dialog nested inside a modal/drawer (<dialog> / [role=dialog]) belongs
 * to a quick-view of a DIFFERENT product, so prefer one that isn't; fall back to
 * the first openable dialog.
 */
function getZoomDialog() {
  const dialogs = Array.from(document.querySelectorAll('zoom-dialog')).filter(
    (zoom) => typeof zoom.open === 'function'
  );
  return dialogs.find((zoom) => !zoom.closest('dialog, [role="dialog"]')) ?? dialogs[0] ?? null;
}

/** Re-scale a Shopify CDN image URL to a given width (caps at the original). */
function scaledUrl(img, width) {
  const base = img.currentSrc || img.src;
  try {
    const url = new URL(base, window.location.href);
    if (/cdn\.shopify\.com$/.test(url.hostname) || url.searchParams.has('width')) {
      url.searchParams.set('width', String(width));
      return url.href;
    }
  } catch {
    /* non-URL src — fall through */
  }
  return base;
}

/** Aspect ratio of a doc image, from its width/height attrs or natural size. */
function aspectRatio(img) {
  const w = parseFloat(img.getAttribute('width'));
  const h = parseFloat(img.getAttribute('height'));
  if (w > 0 && h > 0) return w / h;
  if (img.naturalWidth > 0 && img.naturalHeight > 0) return img.naturalWidth / img.naturalHeight;
  return 0;
}

/** The doc images eligible to become zoomable, in document order, de-duped. */
function collectDocImages(docRoot) {
  const seen = new Set();
  const out = [];
  for (const img of docRoot.querySelectorAll(DOC_IMAGE_SELECTOR)) {
    if (seen.has(img)) continue;
    seen.add(img);
    if (img.hasAttribute('data-bc-no-zoom')) continue; // explicit opt-out
    if (img.closest('a[href]')) continue; // linked image — respect the link
    if (img.closest('.bc-sewing')) continue; // sewing figures excluded by choice
    const width = img.getBoundingClientRect().width || parseFloat(img.getAttribute('width')) || HI_RES_WIDTH;
    if (width < MIN_ZOOMABLE_WIDTH) continue; // skip tiny inline icons
    out.push(img);
  }
  return out;
}

/** Build a zoom slide for a doc image by cloning an existing image slide. */
function buildSlide(templateSlide, docImg) {
  const slide = templateSlide.cloneNode(true);
  slide.removeAttribute('id');
  slide.setAttribute(SLIDE_MARK, '');

  const slideImg = slide.querySelector('img.product-media__image') || slide.querySelector('img');
  if (!slideImg) return null;

  const hi = scaledUrl(docImg, HI_RES_WIDTH);
  slideImg.removeAttribute('srcset');
  slideImg.removeAttribute('sizes');
  slideImg.removeAttribute('width');
  slideImg.removeAttribute('height');
  slideImg.removeAttribute('data_max_resolution');
  slideImg.setAttribute('loading', 'lazy');
  slideImg.setAttribute('fetchpriority', 'auto');
  slideImg.src = hi;
  slideImg.alt = docImg.alt || '';

  // .product-media is locked to the cloned image's --ratio; override so the doc
  // image keeps its own shape instead of being letterboxed to the template's.
  const productMedia = slide.querySelector('.product-media') || slideImg.parentElement;
  if (productMedia) {
    const ratio = aspectRatio(docImg);
    productMedia.style.aspectRatio = ratio > 0 ? String(ratio) : 'auto';
    productMedia.removeAttribute('data-media-id');
  }
  return slide;
}

/** Build a thumbnail button for a doc image by cloning an existing one. */
function buildThumb(templateThumb, docImg, index) {
  const thumb = templateThumb.cloneNode(true);
  thumb.setAttribute(THUMB_MARK, '');
  thumb.setAttribute('on:click', `/handleThumbnailClick/${index}`);
  thumb.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${index}`);
  thumb.removeAttribute('aria-selected');

  // Reshape the thumbnail box to the doc image's own ratio (the clone carries
  // the template image's --aspect-ratio).
  const ratio = aspectRatio(docImg);
  if (ratio > 0) {
    thumb.style.setProperty('--aspect-ratio', String(ratio));
    thumb.style.setProperty('--gallery-aspect-ratio', String(ratio));
  }

  // Replace the cloned gallery <img> with a brand-new one rather than mutating
  // it in place. The cloned thumbnail image is lazy-loaded with sizes="auto"
  // and is cloned while still unloaded (the zoom dialog starts closed), and in
  // Firefox overriding src / removing srcset on that cloned-but-unloaded lazy
  // element does NOT stick — the lazy-load source selection restores the
  // gallery image, so every doc thumbnail rendered the first product photo
  // (the alt we set survived because alt isn't part of source selection).
  // A fresh <img> carries none of that lazy-load/srcset/sizes="auto" state —
  // the same approach makeThumbButton already uses for the single-image path,
  // which was never affected. Chromium tolerated the in-place mutation, which
  // is why this only showed up in Firefox.
  const oldImg = thumb.querySelector('img');
  const thumbImg = document.createElement('img');
  thumbImg.loading = 'lazy';
  thumbImg.src = scaledUrl(docImg, THUMB_WIDTH);
  thumbImg.alt = docImg.alt || '';
  if (oldImg) oldImg.replaceWith(thumbImg);
  else thumb.appendChild(thumbImg);
  return thumb;
}

/**
 * Build a thumbnail button from scratch, for the single-gallery-image case
 * where Horizon renders no thumbnail strip to clone from. Carries Horizon's
 * thumbnail class for native styling AND our own `bc-doc-zoom-thumb` class,
 * which has a zero-specificity fallback in bc-product-docs.css — so if
 * Horizon's class is ever renamed these still render sized (not unstyled).
 */
function makeThumbButton(index, sourceImg, selected) {
  const thumb = document.createElement('button');
  thumb.type = 'button';
  thumb.className = 'bc-doc-zoom-thumb button button-unstyled dialog-thumbnails-list__thumbnail';
  thumb.setAttribute(THUMB_MARK, '');
  thumb.setAttribute('on:click', `/handleThumbnailClick/${index}`);
  thumb.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${index}`);
  if (selected) thumb.setAttribute('aria-selected', 'true');

  const ratio = sourceImg ? aspectRatio(sourceImg) : 0;
  if (ratio > 0) {
    thumb.style.setProperty('--aspect-ratio', String(ratio));
    thumb.style.setProperty('--gallery-aspect-ratio', String(ratio));
  }

  const img = document.createElement('img');
  img.loading = 'lazy';
  if (sourceImg) {
    img.src = scaledUrl(sourceImg, THUMB_WIDTH);
    img.alt = sourceImg.alt || '';
  }
  thumb.appendChild(img);
  return thumb;
}

/** Mark a doc image as zoomable and record which zoom slide it opens. */
function makeZoomable(docImg, index) {
  docImg.setAttribute(INDEX_ATTR, String(index));
  docImg.setAttribute('role', 'button');
  docImg.setAttribute('tabindex', '0');
  if (!docImg.getAttribute('aria-label')) {
    docImg.setAttribute('aria-label', docImg.alt ? `Zoom image: ${docImg.alt}` : 'Zoom image');
  }
}

function init() {
  const zoom = getZoomDialog();
  if (!zoom) return; // gallery zoom disabled / not on page — leave docs as-is

  const list = zoom.querySelector('.dialog-zoomed-gallery');
  const thumbsContainer = zoom.querySelector('.dialog-thumbnails-list');
  if (!list) return;

  const docRoot = document.getElementById(DOCS_ROOT_ID);
  if (!docRoot) return;

  // Idempotent: drop anything a previous run appended (theme-editor re-renders),
  // and clear stale markers off doc images so indices are recomputed cleanly.
  list.querySelectorAll(`li[${SLIDE_MARK}]`).forEach((node) => node.remove());
  if (thumbsContainer) thumbsContainer.querySelectorAll(`button[${THUMB_MARK}]`).forEach((node) => node.remove());
  docRoot.querySelectorAll(`[${INDEX_ATTR}]`).forEach((node) => node.removeAttribute(INDEX_ATTR));

  // Templates to clone — an existing IMAGE slide (first media may be a video).
  const templateSlide = (list.querySelector('li img.product-media__image') || list.querySelector('li > *')?.parentElement)?.closest('li');
  if (!templateSlide) {
    zoom.removeAttribute(GALLERY_COUNT_ATTR);
    return;
  }

  const originalSlides = list.querySelectorAll(':scope > li');
  const startIndex = originalSlides.length;

  const docImages = collectDocImages(docRoot);
  if (docImages.length === 0) {
    zoom.removeAttribute(GALLERY_COUNT_ATTR);
    return;
  }

  // Mark the gallery/doc boundary for the desync guard (see onZoomMediaSelected).
  zoom.setAttribute(GALLERY_COUNT_ATTR, String(startIndex));

  // Thumbnail strategy. Horizon renders a strip only when the gallery has >1
  // media, and the zoom-dialog keys thumbnails to media by index — so the strip
  // must cover EVERY slide or none.
  //   • strip already 1:1 with media (multi-image gallery) → clone its thumbs.
  //   • no strip but there are gallery slides (single-image gallery) → build a
  //     full strip from scratch so gallery + doc slides are all reachable.
  const originalThumbs = thumbsContainer ? thumbsContainer.querySelectorAll(':scope > button') : [];
  let templateThumb = null;
  let synthesizeStrip = false;
  if (thumbsContainer) {
    if (originalThumbs.length === startIndex && startIndex > 0) {
      templateThumb = originalThumbs[0];
    } else if (originalThumbs.length === 0 && startIndex >= 1) {
      synthesizeStrip = true;
    }
  }

  // Single-image case: lay down thumbnails for the existing gallery slide(s)
  // first so thumbnail indices stay aligned with media indices.
  if (synthesizeStrip) {
    originalSlides.forEach((slide, i) => {
      thumbsContainer.appendChild(makeThumbButton(i, slide.querySelector('img'), i === 0));
    });
  }

  let index = startIndex;
  for (const docImg of docImages) {
    const slide = buildSlide(templateSlide, docImg);
    if (!slide) continue;
    list.appendChild(slide);
    if (templateThumb) {
      thumbsContainer.appendChild(buildThumb(templateThumb, docImg, index));
    } else if (synthesizeStrip) {
      thumbsContainer.appendChild(makeThumbButton(index, docImg, false));
    }
    makeZoomable(docImg, index);
    index += 1;
  }

  // Horizon's Component collects ref="media[]" once at connect time and then
  // only via a MutationObserver it arms inside requestIdleCallback — which runs
  // AFTER we append here, so our doc slides never join refs.media. Left alone,
  // zoom.open(docIndex) reads refs.media[docIndex] === undefined and silently
  // opens on the first slide (and selectThumbnail/handleScroll can't reach doc
  // slides either). updatedCallback() is the Component's public re-sync hook;
  // calling it re-collects refs so the appended slides + thumbnails are indexed.
  zoom.updatedCallback?.();
}

/**
 * Desync guard. zoom-dialog reports the visible slide back to media-gallery via
 * a bubbling `zoom-media:selected` event so the (hidden) gallery stays in sync.
 * For our appended doc slides that index is out of the gallery's range, and
 * media-gallery would clamp/wrap its carousel to a wrong slide. We can't change
 * Horizon's dispatch, but the event bubbles and its listener sits on the
 * zoom-dialog (the target), so a capture-phase listener on document fires first
 * and can stopPropagation() for doc indices — leaving the gallery untouched.
 * Gallery indices pass through normally. Degrades to the old cosmetic desync if
 * Horizon ever renames the event.
 */
function onZoomMediaSelected(event) {
  const zoom = event.target;
  if (!(zoom instanceof Element)) return;
  const galleryCount = parseInt(zoom.getAttribute(GALLERY_COUNT_ATTR) || '', 10);
  if (Number.isNaN(galleryCount)) return; // a zoom-dialog we didn't augment
  const index = event.detail?.index;
  if (typeof index === 'number' && index >= galleryCount) {
    event.stopPropagation();
  }
}

/** Delegated open — reads the live index attr, so re-runs never double-bind. */
function onActivate(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const docImg = target.closest(`[${INDEX_ATTR}]`);
  if (!docImg) return;

  if (event.type === 'keydown') {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
  }

  const zoom = getZoomDialog();
  if (!zoom) return;
  const index = parseInt(docImg.getAttribute(INDEX_ATTR) || '', 10);
  if (Number.isNaN(index)) return;
  zoom.open(index, event);
}

document.addEventListener('click', onActivate);
document.addEventListener('keydown', onActivate);
// Capture phase so this runs before media-gallery's target-phase listener.
document.addEventListener('zoom-media:selected', onZoomMediaSelected, true);

/* ------------------------------------------------------------------ *
 * Thumbnail ⇄ main-view sync (cross-browser hardening).
 *
 * Horizon's zoom-dialog already wires this and it works in desktop Chrome, but
 * the mechanisms are brittle once you leave that path (Safari's modal-<dialog>
 * scroll quirks, touch devices) and we widen the slide set with appended doc
 * images of varied heights:
 *   • the scroll-sync listener is bound to refs.dialog, so when something OTHER
 *     than the <dialog> is the scroll container the active thumbnail never
 *     updates (the reported "top image stays outlined while you scroll");
 *   • handleScroll flags the active thumb via aria-selected but never scrolls
 *     it into view, so on a long strip the outline drifts out of sight;
 *   • thumbnail-click scrolling and the most-visible test both lean on
 *     scrollIntoView / intersectionRatio, whose behaviour around overflow
 *     containers and very tall elements varies across engines.
 *
 * Replacements, both framework-agnostic:
 *   • a persistent IntersectionObserver picks the most-visible slide by
 *     on-screen AREA (robust to our varied doc-image heights, where ratio would
 *     favour a short fully-visible slide over the tall one filling the screen)
 *     and drives the active thumbnail regardless of which element scrolls —
 *     then scrolls that thumbnail into view inside the strip;
 *   • a click handler scrolls the chosen slide into the main view by writing
 *     the scroll container's scrollTop/scrollLeft directly, so it doesn't
 *     depend on each engine's scrollIntoView behaviour or bubble-scroll an
 *     unintended ancestor.
 * Both run alongside Horizon's handlers. Upstream's handleScroll is debounced
 * (fires only after scrolling stops) and at a rest position one slide fills the
 * view, so the two agree there; during a scroll only this observer is live.
 * ------------------------------------------------------------------ */
const SYNC_STATE = '__bcZoomThumbSync';

const motionOK = () => !matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Center `thumb` within the (vertical or horizontal) thumbnail strip only. */
function scrollThumbIntoStrip(strip, thumb, behavior) {
  if (getComputedStyle(strip).flexDirection === 'column') {
    if (strip.scrollHeight <= strip.clientHeight + 1) return; // strip not scrollable
    const top = thumb.offsetTop - (strip.clientHeight - thumb.offsetHeight) / 2;
    strip.scrollTo({ top: Math.max(0, top), behavior });
  } else {
    if (strip.scrollWidth <= strip.clientWidth + 1) return;
    const left = thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
    strip.scrollTo({ left: Math.max(0, left), behavior });
  }
}

/**
 * Scroll a zoom slide into the main view by writing the scroll container's
 * offset directly. Resolves the real scroller (mobile <ul> scrolls on X, the
 * desktop <dialog> on Y) so it works the same in every engine without relying
 * on scrollIntoView resolving the right ancestor.
 */
function scrollSlideIntoMain(slide, behavior) {
  const list = slide.parentElement; // <ul class="dialog-zoomed-gallery">
  const dialog = slide.closest('dialog');

  if (list && list.scrollWidth > list.clientWidth + 1) {
    list.scrollTo({ left: list.scrollLeft + slide.getBoundingClientRect().left - list.getBoundingClientRect().left, behavior });
    return;
  }

  const vScroller =
    dialog && dialog.scrollHeight > dialog.clientHeight + 1
      ? dialog
      : list && list.scrollHeight > list.clientHeight + 1
        ? list
        : dialog;
  if (!vScroller) return;
  vScroller.scrollTo({ top: vScroller.scrollTop + slide.getBoundingClientRect().top - vScroller.getBoundingClientRect().top, behavior });
}

/**
 * Keep the thumbnail strip's active outline in lockstep with the main zoom
 * view, and make thumbnail clicks reliably scroll the main view. Idempotent:
 * re-running tears down the prior observer/listener first.
 */
function wireThumbnailSync(zoom) {
  const list = zoom.querySelector('.dialog-zoomed-gallery');
  const strip = zoom.querySelector('.dialog-thumbnails-list');
  if (!list || !strip) return;

  const slides = Array.from(list.querySelectorAll(':scope > li'));
  const thumbs = () => Array.from(strip.querySelectorAll(':scope > button'));
  if (slides.length < 2 || thumbs().length === 0) return; // nothing to track

  // Tear down a previous wiring (theme-editor re-render / doc-image re-append).
  const prev = zoom[SYNC_STATE];
  if (prev) {
    prev.observer.disconnect();
    strip.removeEventListener('click', prev.onClick);
  }

  const areas = new Array(slides.length).fill(0);
  let active = -1;

  const setActive = (index, behavior) => {
    if (index < 0 || index === active) return;
    active = index;
    const buttons = thumbs();
    buttons.forEach((button, i) => button.setAttribute('aria-selected', `${i === index}`));
    const thumb = buttons[index];
    if (thumb) scrollThumbIntoStrip(strip, thumb, behavior);
  };

  // Rank by visible AREA, not intersectionRatio: our doc slides vary in height,
  // and a short fully-visible slide would otherwise outrank the tall slide that
  // actually fills the screen.
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const i = slides.indexOf(/** @type {HTMLElement} */ (entry.target));
        if (i < 0) continue;
        const rect = entry.intersectionRect;
        areas[i] = entry.isIntersecting ? rect.width * rect.height : 0;
      }
      let best = 0;
      let index = -1;
      for (let i = 0; i < areas.length; i++) {
        if (areas[i] > best) {
          best = areas[i];
          index = i;
        }
      }
      setActive(index, motionOK() ? 'smooth' : 'auto');
    },
    { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] }
  );
  slides.forEach((slide) => observer.observe(slide));

  const onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('button');
    if (!button || !strip.contains(button)) return;
    const index = thumbs().indexOf(button);
    if (index < 0 || !slides[index]) return;
    const behavior = motionOK() ? 'smooth' : 'auto';
    scrollSlideIntoMain(slides[index], behavior);
    setActive(index, behavior);
  };
  strip.addEventListener('click', onClick);

  zoom[SYNC_STATE] = { observer, onClick };
}

/* ------------------------------------------------------------------ *
 * Mobile swipe-to-navigate.
 *
 * On mobile every slide is wrapped in Horizon's <drag-zoom-wrapper>, which
 * captures all touches. We open it at 1x (see the bc-mod in drag-zoom-wrapper.js)
 * so that, un-zoomed, a one-finger drag is clamped to no movement. This handler
 * turns a horizontal swipe at 1x into prev/next navigation; once the user
 * pinch/double-taps to zoom IN (scale > 1) it stands down and lets drag-zoom pan.
 * Navigation reuses the same programmatic scroll as a thumbnail tap, so the
 * gallery's IntersectionObserver keeps the active thumbnail in sync.
 * ------------------------------------------------------------------ */
const SWIPE_STATE = '__bcZoomSwipe';
const SWIPE_MIN_PX = 40; // horizontal travel before it counts as a swipe
const SWIPE_MAX_ZOOM = 1.05; // above this the image is zoomed in → pan, don't navigate
const SWIPE_SETTLE_MS = 400; // window after a swipe-scroll where scrollLeft is mid-animation
const MOBILE_QUERY = '(max-width: 749px)';

/** Current on-screen scale of a slide's drag-zoom wrapper (1 when un-zoomed). */
function slideScale(slide) {
  const wrapper = slide?.querySelector('drag-zoom-wrapper');
  if (!wrapper) return 1;
  const value = parseFloat(getComputedStyle(wrapper).getPropertyValue('--drag-zoom-scale'));
  return Number.isFinite(value) && value > 0 ? value : 1;
}

function wireMobileSwipe(zoom) {
  const dialog = zoom.querySelector('.dialog-zoomed-gallery__dialog') || zoom.querySelector('dialog');
  const list = zoom.querySelector('.dialog-zoomed-gallery');
  if (!dialog || !list) return;

  const prev = zoom[SWIPE_STATE];
  if (prev) {
    dialog.removeEventListener('touchstart', prev.onStart, true);
    dialog.removeEventListener('touchend', prev.onEnd, true);
  }

  let startX = 0;
  let startY = 0;
  let tracking = false;
  // Index our own last swipe scrolled toward, so a rapid second swipe steps from
  // the intended target rather than the mid-animation scrollLeft.
  let pendingIndex = -1;
  let pendingTime = 0;

  const onStart = (event) => {
    if (event.touches.length !== 1) {
      tracking = false; // multi-touch (pinch) — let drag-zoom handle it
      return;
    }
    const touch = event.touches[0];
    startX = touch.clientX;
    startY = touch.clientY;
    tracking = true;
  };

  const onEnd = (event) => {
    if (!tracking) return;
    tracking = false;
    if (!matchMedia(MOBILE_QUERY).matches) return;

    const touch = event.changedTouches[0];
    if (!touch) return;
    const dx = touch.clientX - startX;
    const dy = touch.clientY - startY;
    // require a clearly horizontal swipe
    if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy) * 1.5) return;

    const slides = Array.from(list.querySelectorAll(':scope > li'));
    if (slides.length < 2) return;

    const width = list.clientWidth || 1;
    // While our own smooth scroll is still settling, scrollLeft is mid-animation;
    // trust the last target index so back-to-back swipes step cleanly.
    const now = performance.now();
    const current =
      pendingIndex >= 0 && now - pendingTime < SWIPE_SETTLE_MS
        ? pendingIndex
        : Math.round(list.scrollLeft / width);
    // don't hijack the gesture while the current image is zoomed in
    if (slideScale(slides[current]) > SWIPE_MAX_ZOOM) return;

    const next = Math.min(slides.length - 1, Math.max(0, current + (dx < 0 ? 1 : -1)));
    if (next === current || !slides[next]) return;
    scrollSlideIntoMain(slides[next], motionOK() ? 'smooth' : 'auto');
    pendingIndex = next;
    pendingTime = now;
  };

  dialog.addEventListener('touchstart', onStart, { passive: true, capture: true });
  dialog.addEventListener('touchend', onEnd, { passive: true, capture: true });
  zoom[SWIPE_STATE] = { onStart, onEnd };
}

// The product gallery's zoom-dialog upgrades asynchronously; wait for it, then
// run once the DOM is parsed. Re-run on theme-editor section loads.
function start() {
  customElements.whenDefined('zoom-dialog').then(() => {
    init(); // fold doc images into the zoom list (may early-return)
    const zoom = getZoomDialog(); // sync the strip regardless of doc images
    if (zoom) {
      wireThumbnailSync(zoom);
      wireMobileSwipe(zoom);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}

document.addEventListener('shopify:section:load', start);
